import { lazy, Suspense, useEffect } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import LoginPage from "../users/pages/LoginPage";
import SignUpPage from "../users/pages/SignUpPage";
import ForgotPasswordPage from "../users/pages/ForgotPasswordPage";
import ResetPasswordPage from "../users/pages/ResetPasswordPage";
import EmailVerificationPage from "../users/pages/EmailVerificationPage";
import AuthCallbackPage from "../users/pages/AuthCallbackPage";
import OAuthConsentPage from "../users/pages/OAuthConsentPage";
import MapPage from "../map/pages/MapPage";
import CollectionPage from "../cards/pages/CollectionPage";
import TradesPage from "../trades/TradesPage";
import NotFound from "../layout/components/NotFound";
import RequireAuth from "./RequireAuth";
import RequireAdmin from "./RequireAdmin";
import RouteLoadingFallback from "./RouteLoadingFallback";
import { useUser } from "../users/providers/UserProvider";
import ROUTES from "./routesModel";
import useAuthDeepLink from "../users/hooks/useAuthDeepLink";
import AndroidBackHandler from "../native/backButton";
import { isNativePlatform } from "../lib/nativeAuth";
import { readDraft, saveDraftSoon, clearDrafts } from "../native/drafts";
import { isSafeInternalDestination } from "./intendedDestination";

// The Pokémon card browser and every admin page are heavy (MUI data grids,
// image-heavy lists) and are not needed for the first screen any tester
// sees — split them out of the main chunk so a login/map load doesn't pay
// for admin tooling. See #69.
const PokemonMain = lazy(() => import("../cards/components/Pokemon/PokemonMain"));
const UsersManagment = lazy(() => import("../users/components/UsersManagment"));
const EmailManagementPage = lazy(() => import("../users/pages/admin/EmailManagementPage"));
const AdminTradesPage = lazy(() => import("../users/pages/admin/AdminTradesPage"));
const AdminModerationPage = lazy(() => import("../moderation/AdminModerationPage"));
const AdminReportsPage = lazy(() => import("../users/pages/admin/AdminReportsPage"));
const AdminErrorsPage = lazy(() => import("../users/pages/admin/AdminErrorsPage"));
const AdminFeedbackPage = lazy(() => import("../users/pages/admin/AdminFeedbackPage"));
const AdminSafeSpotsPage = lazy(() => import("../users/pages/admin/AdminSafeSpotsPage"));
const AdminSettingsPage = lazy(() => import("../users/pages/admin/AdminSettingsPage"));
const AdminDeletionRequestsPage = lazy(() =>
  import("../users/pages/admin/AdminDeletionRequestsPage")
);
const AdminChallengeReviewPage = lazy(() => import("../verification/AdminChallengeReviewPage"));

// Everything below is one step in from the first screen. Each pulls a wide
// slice of MUI (dialogs, menus, badges, the avatar cropper, the trade rail),
// none of it is on the path a tester takes to cold-start and log in, and all of
// it was statically imported — so every cold load paid for pages most sessions
// never open. Splitting them is what keeps main.js under the CI gate
// `Enforce JS bundle-size budget` (512,000 B gzipped), which had drifted to
// roughly 450 bytes of headroom.
//
// What stays eagerly imported, on purpose:
//   - MapPage    — "/" redirects a signed-in user here, so it IS the native
//                  cold-start screen. Splitting it would trade a CI gate for a
//                  visible launch regression.
//   - LoginPage  — the same, for a signed-out cold start.
//   - ResetPasswordPage, EmailVerificationPage, AuthCallbackPage,
//     OAuthConsentPage, SignUpPage — deep-link and sign-up entry points, each
//                  of which can be the first screen of a session.
//   - CollectionPage, TradesPage — the two bottom-nav tabs nearest the map in
//                  ordinary use; leaving them eager keeps the common taps
//                  instant.
//   - NotFound   — the "*" fallback; it has to be able to render immediately.
const ChatPage = lazy(() => import("../chat/ChatPage"));
const EditUserPage = lazy(() => import("../users/pages/EditUserPage"));
const WishlistPage = lazy(() => import("../wishlist/pages/WishlistPage"));
const TradeJourneyPage = lazy(() => import("../trades/TradeJourneyPage"));
const UserProfile = lazy(() => import("../users/components/UserProfile"));
const PostsPage = lazy(() => import("../posts/PostsPage"));
const LegalPage = lazy(() => import("../legal/pages/LegalPage"));
const DeleteAccountRequestPage = lazy(() =>
  import("../users/pages/DeleteAccountRequestPage")
);
const TradeListLanding = lazy(() => import("../tradeList/TradeListLanding"));

const lazyRoute = (element) => (
  <Suspense fallback={<RouteLoadingFallback />}>{element}</Suspense>
);

/**
 * Where "/" sends you. Gates on `authReady` for the same reason RequireAuth and
 * RequireAdmin do: before hydration a null `user` means "not known yet", not
 * "signed out". Without the gate, every returning user past the 8-hour app-JWT
 * expiry gets bounced to the logged-out marketing page — and since `/` is the
 * Capacitor cold-start URL, that is the ordinary native launch, not an edge case.
 *
 * Keyed purely on `authReady`, with no timeout of its own: UserProvider owns
 * the deadline (AUTH_READY_TIMEOUT_MS) and flips the flag on every outcome of
 * the session lookup — resolved, rejected, or never — so this cannot hang.
 * Before #2 that was only true of the resolved path, and the other two left
 * this returning null forever.
 */
const RootRedirect = ({ authReady, user }) => {
  if (!authReady) return null;
  if (!user) return <Navigate to={ROUTES.LOGIN} replace />;
  // B-59: on a cold start, the screen Android closed the app on (RouteMemory).
  const back = coldStart.pending && isNativePlatform() && readDraft("route", user._id);
  return <Navigate to={isSafeInternalDestination(back) ? back : ROUTES.MAP} replace />;
};

/**
 * The screens worth reopening after Android closes the app in the background
 * (B-59): the ones a collector is in the middle of something on. The map is the
 * default landing anyway; sign-in, password-reset and verify links carry tokens
 * and must never be stored; admin pages and the profile editor start fresh.
 * `/chat` keeps its query, which is how the open conversation is addressed.
 */
export const RESUMABLE_ROUTE = /^\/(collection|wishlist|pokemontcg|posts|chat|trades(\/\w+)?|profile\/\w+)$/;

/**
 * True until the app first shows a screen other than "/".
 *
 * The native shell always starts the WebView at "/", so the first resolution of
 * "/" is the cold start, and the only moment a saved screen may be restored.
 * Every later "/" is an in-app navigation (Back's fallback on a deep-linked
 * screen, the profile editor's save, NotFound) and must still land on the map:
 * Back on a restored /trades/<id> would otherwise loop straight back into it.
 * Module state on purpose, not a ref: Layout renders the router in a different
 * tree on auth routes, so a ref would reset at every sign-in. Exported for tests.
 */
export const coldStart = { pending: true };

/**
 * Remembers the current screen for the signed-in user (B-59). Native only: on
 * the web the browser already keeps the URL across a reload.
 */
const RouteMemory = ({ userId }) => {
  const { pathname, search } = useLocation();
  useEffect(() => {
    if (pathname === ROUTES.ROOT) return;
    if (coldStart.pending) {
      coldStart.pending = false;
      clearDrafts(true);
    }
    if (userId && isNativePlatform()) {
      saveDraftSoon("route", userId, RESUMABLE_ROUTE.test(pathname) ? pathname + search : null);
    }
  }, [pathname, search, userId]);
  return null;
};

const Router = () => {
  const { user, authReady } = useUser();

  // Native-only: route returning OAuth / password-reset deep links into the
  // in-app callback. No-op on web. Lives here so it sits inside the router
  // context it needs for navigation (#83).
  useAuthDeepLink();

  return (
    <>
      {/* Native-only: Android's hardware Back closes the top sheet, then the
          keyboard, then unwinds the router, and only then leaves the app.
          No-op on web. Here for the same reason as useAuthDeepLink — it
          navigates, so it has to sit inside the router context. */}
      <AndroidBackHandler />
      <RouteMemory userId={user?._id} />
      <Routes>
        {/* Public routes */}
        <Route path={ROUTES.LOGIN} element={<LoginPage />} />
        <Route path={ROUTES.SIGNUP} element={<SignUpPage />} />
        <Route path={ROUTES.FORGOT_PASSWORD} element={<ForgotPasswordPage />} />
        <Route path={ROUTES.RESET_PASSWORD} element={<ResetPasswordPage />} />
        <Route path={ROUTES.VERIFY_EMAIL} element={<EmailVerificationPage />} />
        <Route path={ROUTES.VERIFY_EMAIL_HELP} element={<EmailVerificationPage />} />
        <Route path={ROUTES.AUTH_CALLBACK} element={<AuthCallbackPage />} />
        <Route path={ROUTES.OAUTH_CONSENT} element={<OAuthConsentPage />} />
        <Route path={ROUTES.DELETE_ACCOUNT_REQUEST} element={lazyRoute(<DeleteAccountRequestPage />)} />
        {/* Public legal pages (#65): reachable signed in or out, and inside the
            Capacitor wrap where an external link would leave the app. */}
        <Route path={ROUTES.PRIVACY} element={lazyRoute(<LegalPage docKey="privacy" />)} />
        <Route path={ROUTES.TERMS} element={lazyRoute(<LegalPage docKey="terms" />)} />
        {/* A shared trade-list link. Public: a signed-out web visitor is sent
            on to the server's page (TradeListLanding explains when). */}
        <Route path={ROUTES.TRADE_LIST_SHARED} element={lazyRoute(<TradeListLanding publicEntry />)} />

        {/* Root: redirect to map if logged in, else to login */}
        <Route path={ROUTES.ROOT} element={<RootRedirect authReady={authReady} user={user} />} />

        {/* Protected routes */}
        <Route path={ROUTES.MAP} element={<RequireAuth><MapPage /></RequireAuth>} />
        <Route path={ROUTES.POKEMON_CARDS} element={<RequireAuth>{lazyRoute(<PokemonMain />)}</RequireAuth>} />
        <Route path={ROUTES.WISHLIST} element={<RequireAuth>{lazyRoute(<WishlistPage />)}</RequireAuth>} />
        <Route path={ROUTES.COLLECTION} element={<RequireAuth><CollectionPage /></RequireAuth>} />
        <Route path={ROUTES.TRADES} element={<RequireAuth><TradesPage /></RequireAuth>} />
        <Route path={ROUTES.CHAT} element={<RequireAuth>{lazyRoute(<ChatPage />)}</RequireAuth>} />
        <Route path={ROUTES.TRADE_DETAIL} element={<RequireAuth>{lazyRoute(<TradeJourneyPage />)}</RequireAuth>} />
        <Route path={ROUTES.EDIT_USER} element={<RequireAuth>{lazyRoute(<EditUserPage />)}</RequireAuth>} />
        <Route path={`${ROUTES.PROFILE}/:user_id`} element={<RequireAuth>{lazyRoute(<UserProfile />)}</RequireAuth>} />
        <Route path={ROUTES.TRADE_LIST_OFFER} element={<RequireAuth>{lazyRoute(<TradeListLanding />)}</RequireAuth>} />
        <Route path={ROUTES.ADMIN} element={<RequireAdmin>{lazyRoute(<UsersManagment />)}</RequireAdmin>} />
        <Route path={ROUTES.ADMIN_EMAILS} element={<RequireAdmin>{lazyRoute(<EmailManagementPage />)}</RequireAdmin>} />
        <Route path={ROUTES.ADMIN_TRADES} element={<RequireAdmin>{lazyRoute(<AdminTradesPage />)}</RequireAdmin>} />
        <Route path={ROUTES.ADMIN_MODERATION} element={<RequireAdmin>{lazyRoute(<AdminModerationPage />)}</RequireAdmin>} />
        <Route path={ROUTES.ADMIN_REPORTS} element={<RequireAdmin>{lazyRoute(<AdminReportsPage />)}</RequireAdmin>} />
        <Route path={ROUTES.ADMIN_ERRORS} element={<RequireAdmin>{lazyRoute(<AdminErrorsPage />)}</RequireAdmin>} />
        <Route path={ROUTES.ADMIN_FEEDBACK} element={<RequireAdmin>{lazyRoute(<AdminFeedbackPage />)}</RequireAdmin>} />
        <Route path={ROUTES.ADMIN_SAFE_SPOTS} element={<RequireAdmin>{lazyRoute(<AdminSafeSpotsPage />)}</RequireAdmin>} />
        <Route path={ROUTES.ADMIN_DELETION_REQUESTS} element={<RequireAdmin>{lazyRoute(<AdminDeletionRequestsPage />)}</RequireAdmin>} />
        <Route path={ROUTES.ADMIN_CHALLENGE_REVIEW} element={<RequireAdmin>{lazyRoute(<AdminChallengeReviewPage />)}</RequireAdmin>} />
        <Route path={ROUTES.ADMIN_SETTINGS} element={<RequireAdmin>{lazyRoute(<AdminSettingsPage />)}</RequireAdmin>} />
        <Route path="/posts" element={<RequireAuth>{lazyRoute(<PostsPage />)}</RequireAuth>} />

        <Route path="*" element={<NotFound />} />
      </Routes>
    </>
  );
};

export default Router;
